import React from 'react';
import { Card, CardBody, CardHeader, Spinner } from 'reactstrap';
import { useTranslator } from '@front-activerse/common-components';
import type { WearableActivitySummary } from '@front-activerse/common-components';
import type { ApolloError } from '@apollo/client';
import type { DateRange } from '../types';
import { toChartSeries } from '../helpers/series';
import MetricValue from './MetricValue';
import MetricChart from './MetricChart';
import ProviderDeviceChip from './ProviderDeviceChip';

interface ActivityMetric {
  key: keyof WearableActivitySummary;
  labelKey: string;
  unitKey: string;
}

const ACTIVITY_METRICS: ActivityMetric[] = [
  {
    key: 'steps',
    labelKey: 'wearablesDashboard.metric.steps',
    unitKey: 'wearablesDashboard.unit.steps',
  },
  {
    key: 'distanceMeters',
    labelKey: 'wearablesDashboard.metric.distance',
    unitKey: 'wearablesDashboard.unit.meters',
  },
  {
    key: 'floorsClimbed',
    labelKey: 'wearablesDashboard.metric.floors',
    unitKey: 'wearablesDashboard.unit.floors',
  },
  {
    key: 'elevationMeters',
    labelKey: 'wearablesDashboard.metric.elevation',
    unitKey: 'wearablesDashboard.unit.meters',
  },
  {
    key: 'activeCaloriesKcal',
    labelKey: 'wearablesDashboard.metric.activeCalories',
    unitKey: 'wearablesDashboard.unit.kcal',
  },
  {
    key: 'totalCaloriesKcal',
    labelKey: 'wearablesDashboard.metric.totalCalories',
    unitKey: 'wearablesDashboard.unit.kcal',
  },
  {
    key: 'activeMinutes',
    labelKey: 'wearablesDashboard.metric.activeMinutes',
    unitKey: 'wearablesDashboard.unit.minutes',
  },
  {
    key: 'sedentaryMinutes',
    labelKey: 'wearablesDashboard.metric.sedentaryMinutes',
    unitKey: 'wearablesDashboard.unit.minutes',
  },
  {
    key: 'lightIntensityMinutes',
    labelKey: 'wearablesDashboard.metric.lightMinutes',
    unitKey: 'wearablesDashboard.unit.minutes',
  },
  {
    key: 'moderateIntensityMinutes',
    labelKey: 'wearablesDashboard.metric.moderateMinutes',
    unitKey: 'wearablesDashboard.unit.minutes',
  },
  {
    key: 'vigorousIntensityMinutes',
    labelKey: 'wearablesDashboard.metric.vigorousMinutes',
    unitKey: 'wearablesDashboard.unit.minutes',
  },
  {
    key: 'heartRateAvgBpm',
    labelKey: 'wearablesDashboard.metric.hrAvg',
    unitKey: 'wearablesDashboard.unit.bpm',
  },
  {
    key: 'heartRateMaxBpm',
    labelKey: 'wearablesDashboard.metric.hrMax',
    unitKey: 'wearablesDashboard.unit.bpm',
  },
  {
    key: 'heartRateMinBpm',
    labelKey: 'wearablesDashboard.metric.hrMin',
    unitKey: 'wearablesDashboard.unit.bpm',
  },
];

interface Props {
  range: DateRange;
  loading: boolean;
  error: ApolloError | undefined;
  activitySummary: WearableActivitySummary[];
}

const ActivitySummarySection: React.FC<Props> = ({ range, loading, error, activitySummary }) => {
  const { t } = useTranslator();
  const dayRow = activitySummary[activitySummary.length - 1] ?? null;

  return (
    <Card className="mb-4" data-testid="activity-summary-section">
      <CardHeader>{t('wearablesDashboard.section.activity.title')}</CardHeader>
      <CardBody>
        {loading && !dayRow ? <Spinner /> : null}
        {error ? (
          <div data-testid="activity-summary-error" className="text-danger">
            {t('wearablesDashboard.error.section')}: {error.message}
          </div>
        ) : null}
        {!loading && !error && activitySummary.length === 0 ? (
          <div data-testid="activity-summary-empty">
            {range.isSingleDay
              ? t('wearablesDashboard.empty.day')
              : t('wearablesDashboard.empty.range')}
          </div>
        ) : null}
        {range.isSingleDay && dayRow
          ? ACTIVITY_METRICS.map((metric) => (
              <MetricValue
                key={metric.key as string}
                labelKey={metric.labelKey}
                unitKey={metric.unitKey}
                value={dayRow[metric.key] as number | null}
                provider={dayRow.provider}
                device={dayRow.device}
              />
            ))
          : null}
        {!range.isSingleDay && activitySummary.length > 0 ? (
          <>
            {(() => {
              const lastWithProvider = [...activitySummary].reverse().find((row) => row.provider);
              return lastWithProvider ? (
                <div className="mb-3">
                  <ProviderDeviceChip
                    provider={lastWithProvider.provider}
                    device={lastWithProvider.device}
                  />
                </div>
              ) : null;
            })()}
            {ACTIVITY_METRICS.map((metric) => (
              <div key={metric.key as string} className="mb-4">
                <small className="text-muted d-block mb-1">{t(metric.labelKey)}</small>
                <MetricChart
                  points={toChartSeries(
                    activitySummary,
                    range,
                    (row) => row[metric.key] as number | null
                  )}
                  ariaLabel={t(metric.labelKey)}
                  unit={t(metric.unitKey)}
                />
              </div>
            ))}
          </>
        ) : null}
      </CardBody>
    </Card>
  );
};

export default ActivitySummarySection;
export { ACTIVITY_METRICS };
